import qs from "query-string";

import * as api from "../../../apiv2/planner/orders";
import { handleError } from "../../utils/error";
import { hasEmptiedServerFilterGroup } from "../filter.helpers";
import * as labelFilterSelectors from "../labelFilter/labelFilter.selectors";
import * as requiredDateSelectors from "../requiredDateFilter/requiredDate.selectors";
import {
  EMPTY_SCHEDULE_STATE_COUNTS,
  SCHEDULE_STATE_ALL,
} from "../scheduleStateFilter/scheduleStateFilter.constants";
import * as scheduleStateFilterSelectors from "../scheduleStateFilter/scheduleStateFilter.selectors";
import * as searchFilterSelectors from "../searchFilter/searchFilter.selectors";
import * as statusFilterSelectors from "../statusFilter/statusFilter.selectors";
import * as userFilterSelectors from "../userFilter/userFilter.selectors";

import {
  RESET,
  SET_HAS_FETCHED,
  SET_HAS_NEXT_PAGE,
  SET_IS_FETCHING_ORDERS,
  SET_IS_REPLACING_ORDERS,
  SET_OFFSET,
  SET_ORDERS,
  SET_SCHEDULE_STATE_COUNTS,
  SET_TOTAL,
} from "./orders.actionTypes";
import * as selectors from "./orders.selectors";

// The rail's page size. fetchOrders takes it as an argument so a refresh can ask for the whole
// loaded window instead of collapsing the rail back to the first page.
const PAGE_SIZE = 20;

// Not a workflow status: the backend reads it as "also return soft-deleted orders".
const ARCHIVED_STATUS = "Archived";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingOrders = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_ORDERS,
    payload: status,
  });
};

export const setIsReplacingOrders = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_REPLACING_ORDERS,
    payload: status,
  });
};

export const setOrders = (data) => (dispatch) => {
  dispatch({
    type: SET_ORDERS,
    payload: data,
  });
};

export const setOffset = (data) => (dispatch) => {
  dispatch({
    type: SET_OFFSET,
    payload: data,
  });
};

export const setTotal = (data) => (dispatch) => {
  dispatch({
    type: SET_TOTAL,
    payload: data,
  });
};

export const setHasFetched = (data) => (dispatch) => {
  dispatch({
    type: SET_HAS_FETCHED,
    payload: data,
  });
};

export const setHasNextPage = (data) => (dispatch) => {
  dispatch({
    type: SET_HAS_NEXT_PAGE,
    payload: data,
  });
};

export const setScheduleStateCounts = (data) => (dispatch) => {
  dispatch({
    type: SET_SCHEDULE_STATE_COUNTS,
    payload: data,
  });
};

// Identity of the newest fetchOrders call. Replies from any older call are dropped wholesale:
// two rapid tab clicks each start a replacing fetch, and without this the first reply to land
// uncovers the rail and the last reply to land wins the rows - wrong on both counts when they
// invert. Only the newest call may write results or lower the flags it raised.
let newestFetchId = 0;

export const fetchOrders =
  ({ limit = PAGE_SIZE, background = false } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const fetchId = ++newestFetchId;
    const selectedUsers = userFilterSelectors.getSelectedUsers(state);
    const selectedLabels = labelFilterSelectors.getSelectedLabels(state);
    const selectedStatuses = statusFilterSelectors.getSelectedStatuses(state);
    const search = searchFilterSelectors.getSearch(state);
    const scheduleState = scheduleStateFilterSelectors.getScheduleState(state);
    const requiredStartDate = requiredDateSelectors.getRequiredStartDate(state);
    const requiredEndDate = requiredDateSelectors.getRequiredEndDate(state);
    const users = userFilterSelectors.getUsers(state);
    const labels = labelFilterSelectors.getLabels(state);
    const statuses = statusFilterSelectors.getStatuses(state);
    const offset = selectors.getPagination(state).offset;

    // "All" is a frontend-only sentinel: the server has no "all" value and 400s on one, so the
    // param is omitted rather than sent.
    const scheduleStateParam = scheduleState === SCHEDULE_STATE_ALL ? undefined : scheduleState;

    // The counts describe the whole matching set, so they are identical on every page. Ask for
    // them on the first page only; a load-more reply carries no counts key.
    const includeScheduleStateCounts = offset === 0 ? true : undefined;

    // The rail never draws an archived order, but the backend reads "Archived" as "also return
    // soft-deleted orders". Sending it meant the schedule-state badges and the Orders total
    // counted rows that could never become cards. Dropped here, for the rail only - the board's
    // own request still sends it, so ticking Archived keeps working on the calendar.
    const railStatuses = selectedStatuses.filter((name) => name !== ARCHIVED_STATUS);
    const isArchivedOnly = selectedStatuses.length > 0 && railStatuses.length === 0;

    // Empty a server-side group (assignee/labels/status) => empty rail. The backend reads an empty
    // list as "no filter", so the client short-circuits. This holds during a search too: a search
    // stays within the active filters (CUS-574), so no statuses plus a query is still nothing.
    // Archived on its own lands here too: it leaves the rail nothing to show, and sending no
    // statuses would read as "no filter" and return the lot.
    if (
      isArchivedOnly ||
      hasEmptiedServerFilterGroup({
        users,
        selectedUsers,
        labels,
        selectedLabels,
        statuses,
        selectedStatuses,
      })
    ) {
      dispatch(setOrders([]));
      dispatch(setTotal(0));
      dispatch(setHasNextPage(false));
      // No request means no fresh counts. Zero them, or the badges keep describing a set the
      // empty rail contradicts.
      dispatch(setScheduleStateCounts(EMPTY_SCHEDULE_STATE_COUNTS));
      dispatch(setHasFetched(true));
      // This branch returns before the try/finally that normally lowers these, so any fetch it
      // invalidates (in-flight or about to raise its own flags) would otherwise strand them up.
      dispatch(setIsFetchingOrders(false));
      dispatch(setIsReplacingOrders(false));
      return;
    }

    // A search stays within the active filters (CUS-574, reversing the earlier "search supersedes
    // filters" decision): the query goes out with every group, so the rail and the tab counts,
    // which the server aggregates on the same set, describe the filtered matches.
    const filters = {
      limit,
      offset,
      search: search !== "" ? search : undefined,
      assignees: selectedUsers.length > 0 ? selectedUsers : undefined,
      labels: selectedLabels.length > 0 ? selectedLabels : undefined,
      statuses: railStatuses.length > 0 ? railStatuses : undefined,
      requiredStartDate: requiredStartDate || undefined,
      requiredEndDate: requiredEndDate || undefined,
      include_item_count: true,
      include_assignee_search: true,
      // Adds is_scheduled/scheduled_entry_uid to each assignee, so the drawer can tell whether
      // someone is scheduled on ANY date rather than only within the dates the board has loaded.
      include_assignee_schedule_state: true,
      schedule_state: scheduleStateParam,
      include_schedule_state_counts: includeScheduleStateCounts,
    };

    // Drives the loader that covers the rail. Offset 0 is what marks a fetch that throws the whole
    // list away rather than appending a page; hasFetched excludes the first load, which the
    // page-level loader already covers; background excludes refreshes the user never asked for.
    const isReplacing = offset === 0 && selectors.getHasFetched(state) && !background;

    dispatch(setIsFetchingOrders(true));
    if (isReplacing) {
      dispatch(setIsReplacingOrders(true));
    }
    try {
      const { data } = await api.getOrders(filters)();
      if (fetchId !== newestFetchId) {
        return;
      }
      // Read fresh, not the pre-await snapshot: an in-flight optimistic update (e.g. an
      // assignee change) that landed while this request was out must survive the append.
      const currentOrders = selectors.getOrders(getState());
      // Belt-and-braces against offset drift: drop any row already present before concatenating,
      // so a duplicate uid never lands as a duplicate React key.
      const existingUids = new Set(currentOrders.map((order) => order.uid));
      const newResults =
        offset === 0 ? data.results : data.results.filter((order) => !existingUids.has(order.uid));
      const allOrders = offset === 0 ? [...newResults] : [...currentOrders, ...newResults];

      dispatch(
        setOrders(
          allOrders.map((d) => ({
            ...d,
            length: "1.0h",
            start: "00:00",
            end: "01:00",
          }))
        )
      );
      dispatch(setTotal(data.count));
      // Only when the response actually carries them. A load-more reply has no counts key, and
      // writing undefined would blank badges that are still correct.
      if (data.scheduleStateCounts) {
        dispatch(setScheduleStateCounts(data.scheduleStateCounts));
      }
      dispatch(setHasNextPage(Boolean(data.next)));
      if (data.next) {
        const parsedNextPage = qs.parse(data.next);
        dispatch(setOffset(parsedNextPage?.offset ?? 0));
      }
    } catch (error) {
      if (fetchId !== newestFetchId) {
        return;
      }
      handleError(error, dispatch);
    } finally {
      // A stale fetch that lands after a newer one has already taken over must not touch the
      // flags the newer fetch owns now - return above still runs this block, so guard it too.
      if (fetchId === newestFetchId) {
        dispatch(setIsFetchingOrders(false));
        // The newest fetch always clears this, even if it did not raise it itself - it is the
        // only fetch left that could still be covering the rail with the replacing overlay.
        dispatch(setIsReplacingOrders(false));
        dispatch(setHasFetched(true));
      }
    }
  };

export const loadMoreOrders = () => async (dispatch, getState) => {
  const state = getState();
  const { offset } = selectors.getPagination(state);

  dispatch(setOffset(parseInt(offset)));
  dispatch(fetchOrders());
};

// Refetch the rows the rail already has, after something off-screen changed an order - a schedule
// change, or an edit made on the sales order page. Asks for the pages the user already had (offset
// 0, limit covering them) so it does not collapse an 80-order rail back to 20. A plain fetchOrders
// would append the NEXT page instead, leaving every loaded row stale. Refetching is also the ONLY
// way the badges may change: the classification lives on the server and is never recomputed here.
// Runs as a background fetch: the user dropped a card, they did not ask for the rail to reload, so
// covering it with a loader every time would be noise.
export const refreshLoadedOrders = () => (dispatch, getState) => {
  const orders = selectors.getOrders(getState());

  dispatch(setOffset(0));

  return dispatch(fetchOrders({ limit: Math.max(PAGE_SIZE, orders.length), background: true }));
};
